import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Gauge, Euro, Info, ExternalLink, FileSignature } from "lucide-react";
import SEO from "../components/SEO";
import QuotaCalendar, { QuotaGauge } from "../components/telesoin/QuotaCalendar";
import ContractsSection from "../components/telesoin/ContractsSection";
import { API } from "../apiBase";

const YEAR = new Date().getFullYear();

export default function MssanteTelesoin() {
  const [state, setState] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [ca, setCa] = useState("");
  const [tariff, setTariff] = useState("25");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [q, c] = await Promise.all([
          axios.get(`${API}/pro/telesoin/quota?year=${YEAR}`),
          axios.get(`${API}/pro/telesoin/contracts`),
        ]);
        setState(q.data);
        setContracts(c.data || []);
        if (q.data.configured) {
          setCa(String(q.data.ca_n1));
          setTariff(String(q.data.avg_tariff));
        }
      } catch (e) {
        console.error("telesoin load:", e);
        toast.error("Chargement du module télésoin impossible.");
      }
    })();
  }, []);

  const saveQuota = async () => {
    const caNum = parseFloat(ca);
    const tNum = parseFloat(tariff);
    if (!(caNum >= 0) || !(tNum > 0)) {
      toast.error("Saisissez un CA N-1 et un tarif moyen valides.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/pro/telesoin/quota`, { year: YEAR, ca_n1: caNum, avg_tariff: tNum });
      setState(r.data);
      toast.success(`Quota calculé : ${r.data.max_acts} télésoins autorisés en ${YEAR}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const addAct = async (date, patient_ref) => {
    try {
      const r = await axios.post(`${API}/pro/telesoin/acts`, { year: YEAR, date, patient_ref });
      setState(r.data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ajout impossible.");
      return false;
    }
  };

  const deleteAct = async (id) => {
    try {
      const r = await axios.delete(`${API}/pro/telesoin/acts/${id}`);
      setState(r.data);
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="page-telesoin">
      <SEO title="Quota télésoin & contrats" noIndex />
      <section className="pt-28 pb-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Link to="/mssante/espace" data-testid="telesoin-back"
                className="text-[11px] uppercase tracking-[0.18em] text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1 mb-3">
            <ArrowLeft size={12} /> Retour à mon espace
          </Link>
          <h1 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Quota télésoin &amp; contrats
          </h1>
          <p className="mt-2 text-[13.5px] text-[#475569] max-w-3xl leading-relaxed">
            Pilotez vos plafonds réglementaires d'activité à distance et générez vos documents
            officiels (Ordre, contrats de travail, collaborations).
          </p>

          <div className="mt-4 rounded-xl border border-[#BAE6FD] bg-[#F0F9FF] p-4 flex items-start gap-3" data-testid="telesoin-reglement">
            <Info size={16} className="text-[#0284C7] mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-[#0F2B5B] leading-relaxed">
              <b>Rappel réglementaire</b> : les actes de télésanté ne peuvent dépasser <b>20 % de votre
              chiffre d'affaires conventionné annuel</b>, ni <b>4 actes facturés par an pour un même
              patient</b>.{" "}
              <a href="https://www.service-public.gouv.fr/particuliers/vosdroits/F34696" target="_blank" rel="noreferrer"
                 className="text-[#0284C7] underline inline-flex items-center gap-0.5">service-public.fr <ExternalLink size={10} /></a>{" · "}
              <a href="https://www.ameli.fr/assure/remboursements/rembourse/consultations-telemedecine/telemedecine/teleconsultation" target="_blank" rel="noreferrer"
                 className="text-[#0284C7] underline inline-flex items-center gap-0.5">ameli.fr <ExternalLink size={10} /></a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-4 inline-flex items-center gap-2">
            <Gauge size={13} /> Mon quota {YEAR}
          </div>
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 lg:p-6">
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.14em] text-[#475569] mb-1.5">
                  Chiffre d'affaires {YEAR - 1} (€)
                </span>
                <div className="relative">
                  <Euro size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <input value={ca} onChange={(e) => setCa(e.target.value)} type="number" min="0"
                         placeholder="ex : 90000" data-testid="telesoin-ca-input"
                         className="w-full h-10 rounded-lg border border-[#E2E8F0] pl-9 pr-3 text-[14px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]" />
                </div>
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.14em] text-[#475569] mb-1.5">
                  Tarif moyen par acte (€)
                </span>
                <input value={tariff} onChange={(e) => setTariff(e.target.value)} type="number" min="1"
                       data-testid="telesoin-tariff-input"
                       className="w-full h-10 rounded-lg border border-[#E2E8F0] px-3 text-[14px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]" />
              </label>
              <button onClick={saveQuota} disabled={saving} data-testid="telesoin-quota-save"
                      className="btn-primary h-10 px-6 rounded-full text-[13.5px] font-medium disabled:opacity-50">
                {saving ? "Calcul…" : "Calculer mon quota"}
              </button>
            </div>

            {state?.configured && (
              <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="telesoin-quota-results">
                <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#475569]">Plafond CA à distance (20 %)</div>
                  <div className="mt-1 font-display text-[#0F2B5B] text-2xl" data-testid="telesoin-max-ca">
                    {state.max_ca_remote.toLocaleString("fr-FR")} €
                  </div>
                </div>
                <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#475569]">Télésoins autorisés / an</div>
                  <div className="mt-1 font-display text-[#0F2B5B] text-2xl" data-testid="telesoin-max-acts">
                    {state.max_acts}
                  </div>
                </div>
                <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 flex flex-col justify-center">
                  <QuotaGauge used={state.used} max={state.max_acts} label="Consommé cette année" />
                  <div className="mt-2 text-[11px] text-[#475569]">
                    Reste <b className="text-[#0F2B5B]">{state.remaining}</b> actes · ~{state.monthly_targets?.[0] ?? 0}/mois recommandé
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {state && (
        <section className="py-6">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-4">
              Planning annuel — étalez vos télésoins
            </div>
            {!state.configured && (
              <p className="mb-3 text-[12.5px] text-[#B45309]" data-testid="telesoin-configure-first">
                Renseignez d'abord votre CA N-1 ci-dessus pour activer le planning.
              </p>
            )}
            <QuotaCalendar state={state} onAdd={addAct} onDelete={deleteAct} />
          </div>
        </section>
      )}

      <section className="py-6 pb-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-4 inline-flex items-center gap-2">
            <FileSignature size={13} /> Mes contrats officiels
          </div>
          <ContractsSection contracts={contracts} setContracts={setContracts} />
        </div>
      </section>
    </div>
  );
}
